import React from 'react'
import { cn } from '@/utils/cn'
import { useTheme } from '@/hooks/useTheme'

export interface TitleTextProps {
  children: React.ReactNode
  level?: 1 | 2 | 3 | 4 | 5 | 6
  className?: string
  variant?: 'primary' | 'secondary'
  'data-testid'?: string
}

const TitleText: React.FC<TitleTextProps> = ({
  children,
  level = 1,
  className,
  variant = 'primary',
  'data-testid': testId = 'title-text'
}) => {
  const { isDark } = useTheme()
  const baseClasses = "font-heading font-bold"

  const sizeClasses = {
    1: "text-4xl mb-4",
    2: "text-3xl mb-4",
    3: "text-2xl mb-4",
    4: "text-xl mb-3",
    5: "text-lg mb-3",
    6: "text-base mb-2"
  }

  const getVariantClasses = (variant: 'primary' | 'secondary') => {
    if (variant === 'primary') {
      return isDark ? "text-white" : "text-secondary-900"
    }
    return isDark ? "text-secondary-300" : "text-secondary-700"
  }

  const combinedClasses = cn(
    baseClasses,
    sizeClasses[level],
    getVariantClasses(variant),
    className
  )

  switch (level) {
    case 1:
      return <h1 className={combinedClasses} data-testid={testId}>{children}</h1>
    case 2:
      return <h2 className={combinedClasses} data-testid={testId}>{children}</h2>
    case 3:
      return <h3 className={combinedClasses} data-testid={testId}>{children}</h3>
    case 4:
      return <h4 className={combinedClasses} data-testid={testId}>{children}</h4>
    case 5:
      return <h5 className={combinedClasses} data-testid={testId}>{children}</h5>
    case 6:
      return <h6 className={combinedClasses} data-testid={testId}>{children}</h6>
    default:
      return <h1 className={combinedClasses} data-testid={testId}>{children}</h1>
  }
}

export { TitleText }